import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, MenuItem, Button, Alert } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Search as SearchIcon } from '@mui/icons-material';
import { FlujoCajaService } from '../services/flujoCajaService';
import type { FlujoCajaResponse, FlujoCajaFiltro } from '../services/flujoCajaService';

const MESES_NOMBRES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const FlujoCajaPage: React.FC = () => {
  const [filtro, setFiltro] = useState<FlujoCajaFiltro>({
    ejercicio: new Date().getFullYear().toString(),
    periodoInicio: '01',
    periodoFin: '12',
  });
  const [data, setData] = useState<FlujoCajaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConsultar = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await FlujoCajaService.getFlujoCaja(filtro);
      setData(result);
    } catch {
      setError('Error al consultar flujo de caja');
    } finally {
      setLoading(false);
    }
  };

  const baseColumns: GridColDef[] = [
    { field: 'descripcion', headerName: 'Concepto', width: 250, flex: 1 },
    { field: 'tipo', headerName: 'Tipo', width: 80 },
  ];

  const periodoColumns: GridColDef[] = (data?.periodos || []).map((p) => ({
    field: `periodo_${p}`,
    headerName: MESES_NOMBRES[parseInt(p) - 1] || p,
    width: 130,
    type: 'number' as const,
    valueGetter: (_value: unknown, row: Record<string, unknown>) => {
      const montos = row.montosPorPeriodo as Record<string, number> | undefined;
      return montos?.[p] || 0;
    },
    valueFormatter: (value: number) =>
      value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
  }));

  const allColumns = [...baseColumns, ...periodoColumns];

  const rows = (data?.filas || []).map((f, i) => ({ id: i, ...f }));

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Flujo de Caja
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Ejercicio"
            size="small"
            value={filtro.ejercicio}
            onChange={(e) => setFiltro({ ...filtro, ejercicio: e.target.value })}
            sx={{ width: 100 }}
          />
          <TextField
            label="Periodo Inicio"
            select
            size="small"
            value={filtro.periodoInicio}
            onChange={(e) => setFiltro({ ...filtro, periodoInicio: e.target.value })}
            sx={{ width: 150 }}
          >
            {MESES_NOMBRES.map((m, i) => (
              <MenuItem key={i} value={String(i + 1).padStart(2, '0')}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Periodo Fin"
            select
            size="small"
            value={filtro.periodoFin}
            onChange={(e) => setFiltro({ ...filtro, periodoFin: e.target.value })}
            sx={{ width: 150 }}
          >
            {MESES_NOMBRES.map((m, i) => (
              <MenuItem key={i} value={String(i + 1).padStart(2, '0')}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading}
          >
            Consultar
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 260px)' }}>
        <DataGrid
          rows={rows}
          columns={allColumns}
          loading={loading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.row.esSubtotal ? 'row-subtotal' : ''
          }
          sx={{
            '& .row-subtotal': {
              fontWeight: 'bold',
              bgcolor: 'action.hover',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default FlujoCajaPage;
